/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Users, Info, Sparkles } from 'lucide-react';
import { TablePlan, ReservationPlan, Zone } from './types.ts';
import { calculerCapacitesEnDirect } from './logiquePure.ts';
import { LegendeStatuts } from './LegendeStatuts.tsx';

interface BandeauCapaciteProps {
  tables: TablePlan[];
  reservations: ReservationPlan[];
  heureAffichee: string;
  zoneActive: Zone;
  estEnBrouillon: boolean;
}

export const BandeauCapacite: React.FC<BandeauCapaciteProps> = ({
  tables,
  reservations,
  heureAffichee,
  zoneActive,
  estEnBrouillon,
}) => {
  const { couvertsTotaux, couvertsDisponibles, tablesDisponiblesParTaille } =
    calculerCapacitesEnDirect(tables, reservations, heureAffichee, zoneActive.id);

  return (
    <div
      className="relative bg-slate-50 dark:bg-slate-900/90 border-b border-slate-200 dark:border-slate-800 px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2.5 text-xs select-none"
      aria-live="polite"
    >
      {/* Résumé de capacité pour la zone */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-200">
          <Users className="w-4 h-4 text-slate-500" aria-hidden="true" />
          <span>{zoneActive.nom}</span>
          {zoneActive.compteAPart && (
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-100 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
              Compté à part
            </span>
          )}
        </div>

        <div className="h-3 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block" />

        <div className="flex items-center gap-2">
          <span className="text-slate-500 dark:text-slate-400">Couverts :</span>
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {couvertsDisponibles} libres / {couvertsTotaux} max
          </span>
        </div>

        {estEnBrouillon && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 animate-pulse">
            <Sparkles className="w-3 h-3" />
            Recalculé en direct (brouillon)
          </span>
        )}
      </div>

      {/* Détail par taille de groupe */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-slate-500 dark:text-slate-400 mr-1 hidden md:inline">
          Disponibilité par groupe :
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          <BadgeTailleGroupe
            label="2 pers."
            quantite={tablesDisponiblesParTaille['2'] || 0}
          />
          <BadgeTailleGroupe
            label="4 pers."
            quantite={tablesDisponiblesParTaille['4'] || 0}
          />
          <BadgeTailleGroupe
            label="6 pers."
            quantite={tablesDisponiblesParTaille['6'] || 0}
          />
          <BadgeTailleGroupe
            label="8 pers."
            quantite={tablesDisponiblesParTaille['8'] || 0}
          />
          <BadgeTailleGroupe
            label="12+ pers."
            quantite={tablesDisponiblesParTaille['12+'] || 0}
          />
        </div>
      </div>

      <LegendeStatuts />
    </div>
  );
};

const BadgeTailleGroupe: React.FC<{ label: string; quantite: number }> = ({
  label,
  quantite,
}) => {
  const estDisponible = quantite > 0;
  return (
    <span
      className={`px-2 py-0.5 rounded text-[11px] font-medium border flex items-center gap-1 ${
        estDisponible
          ? 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
          : 'bg-slate-100 dark:bg-slate-800/40 text-slate-400 dark:text-slate-500 border-dashed border-slate-200 dark:border-slate-800'
      }`}
      title={`${quantite} table(s) disponible(s) pour ${label}`}
    >
      <span className="font-semibold">{quantite}</span>
      <span className="text-[10px] text-slate-500 dark:text-slate-400">{label}</span>
    </span>
  );
};
